import { create } from "zustand";

// Default colors for table rows
const DEFAULT_COLORS = {
  tradeBlotter: {
    light: "#f9fafb",
    dark: "#1f2937",
    bidLight: "#dcfce7",
    bidDark: "#14532d",
    askLight: "#fee2e2",
    askDark: "#7f1d1d",
    flashLight: "#8bd0c2",
    flashDark: "#1b655e",
  },
  prices: {
    light: "#f9fafb",
    dark: "#1f2937",
    flashLight: "#8bd0c2",
    flashDark: "#1b655e",
  },
  import: {
    light: "#f9fafb",
    dark: "#1f2937",
  },
  creation: {
    light: "#f9fafb",
    dark: "#1f2937",
    bidPayLight: "#dcfce7",
    bidPayDark: "#14532d",
    askReceiveLight: "#fee2e2",
    askReceiveDark: "#7f1d1d",
    flashLight: "#8bd0c2",
    flashDark: "#1b655e",
  },
};

interface IuseTableUIStore {
  tableStripeTradeBlotterOn: boolean;
  tableStripePricesOn: boolean;
  tableStripeImportOn: boolean;
  toggleTableStripeTradeBlotter: () => void; // toggleState is a function that takes no arguments and returns void
  toggleTableStripePrices: () => void; // toggleState is a function that takes no arguments and returns void
  toggleTableStripeImport: () => void; // toggleState is a function that takes no arguments and returns void

  // Font and row size settings
  fontSize: "small" | "medium" | "large" | "x-large";
  rowHeight: "narrow" | "standard" | "expanded";
  setFontSize: (size: "small" | "medium" | "large" | "x-large") => void;
  setRowHeight: (height: "narrow" | "standard" | "expanded") => void;

  // Sound settings
  soundQuoted: string;
  soundAwarded: string;
  soundExecuted: string;
  soundAffirmed: string;
  setSoundQuoted: (sound: string) => void;
  setSoundAwarded: (sound: string) => void;
  setSoundExecuted: (sound: string) => void;
  setSoundAffirmed: (sound: string) => void;

  // Table row colors
  tradeBlotterColorLight: string;
  tradeBlotterColorDark: string;
  pricesColorLight: string;
  pricesColorDark: string;
  importColorLight: string;
  importColorDark: string;
  setTradeBlotterColorLight: (color: string) => void;
  settradeBlotterColorDark: (color: string) => void;
  setPricesColorLight: (color: string) => void;
  setPricesColorDark: (color: string) => void;
  setImportColorLight: (color: string) => void;
  setImportColorDark: (color: string) => void;
  resetTradeBlotterColors: () => void;
  resetPricesColors: () => void;
  resetImportColors: () => void;

  // Bid/Ask colors for Action Summary
  tradeBlotterBidAskEnabled: boolean;
  tradeBlotterBidColorLight: string;
  tradeBlotterBidColorDark: string;
  tradeBlotterAskColorLight: string;
  tradeBlotterAskColorDark: string;
  toggleTradeBlotterBidAsk: () => void;
  setTradeBlotterBidColorLight: (color: string) => void;
  setTradeBlotterBidColorDark: (color: string) => void;
  setTradeBlotterAskColorLight: (color: string) => void;
  setTradeBlotterAskColorDark: (color: string) => void;
  resetTradeBlotterBidAskColors: () => void;

  // Creation Table colors
  creationColorLight: string;
  creationColorDark: string;
  setCreationColorLight: (color: string) => void;
  setCreationColorDark: (color: string) => void;
  resetCreationColors: () => void;

  // Direction colors for Creation Table (Bid/Pay and Ask/Receive)
  creationDirectionEnabled: boolean;
  creationBidPayColorLight: string;
  creationBidPayColorDark: string;
  creationAskReceiveColorLight: string;
  creationAskReceiveColorDark: string;
  toggleCreationDirection: () => void;
  setCreationBidPayColorLight: (color: string) => void;
  setCreationBidPayColorDark: (color: string) => void;
  setCreationAskReceiveColorLight: (color: string) => void;
  setCreationAskReceiveColorDark: (color: string) => void;
  resetCreationDirectionColors: () => void;

  // Flash colours for Trade Blotter
  tradeBlotterFlashColorLight: string;
  tradeBlotterFlashColorDark: string;
  setTradeBlotterFlashColorLight: (color: string) => void;
  setTradeBlotterFlashColorDark: (color: string) => void;
  resetTradeBlotterFlashColors: () => void;

  // Flash colours for Creation Table
  creationFlashColorLight: string;
  creationFlashColorDark: string;
  setCreationFlashColorLight: (color: string) => void;
  setCreationFlashColorDark: (color: string) => void;
  resetCreationFlashColors: () => void;

  // Flash colours for Prices Table
  pricesFlashColorLight: string;
  pricesFlashColorDark: string;
  setPricesFlashColorLight: (color: string) => void;
  setPricesFlashColorDark: (color: string) => void;
  resetPricesFlashColors: () => void;

  // Test flash triggers (counter-based to fire on each click)
  testFlashTradeBlotter: number;
  triggerTestFlashTradeBlotter: () => void;
  testFlashCreation: number;
  triggerTestFlashCreation: () => void;
  testFlashPrices: number;
  triggerTestFlashPrices: () => void;

  initializeState: () => void;
}

const useTableUIStore = create<IuseTableUIStore>((set) => ({
  tableStripeTradeBlotterOn: true,
  tableStripePricesOn: true,
  tableStripeImportOn: true,
  fontSize: "small",
  rowHeight: "narrow",
  soundQuoted: "notification1",
  soundAwarded: "notification2",
  soundExecuted: "notification3",
  soundAffirmed: "notification4",
  tradeBlotterColorLight: DEFAULT_COLORS.tradeBlotter.light,
  tradeBlotterColorDark: DEFAULT_COLORS.tradeBlotter.dark,
  pricesColorLight: DEFAULT_COLORS.prices.light,
  pricesColorDark: DEFAULT_COLORS.prices.dark,
  importColorLight: DEFAULT_COLORS.import.light,
  importColorDark: DEFAULT_COLORS.import.dark,
  tradeBlotterBidAskEnabled: false,
  tradeBlotterBidColorLight: DEFAULT_COLORS.tradeBlotter.bidLight,
  tradeBlotterBidColorDark: DEFAULT_COLORS.tradeBlotter.bidDark,
  tradeBlotterAskColorLight: DEFAULT_COLORS.tradeBlotter.askLight,
  tradeBlotterAskColorDark: DEFAULT_COLORS.tradeBlotter.askDark,
  creationColorLight: DEFAULT_COLORS.creation.light,
  creationColorDark: DEFAULT_COLORS.creation.dark,
  creationDirectionEnabled: false,
  creationBidPayColorLight: DEFAULT_COLORS.creation.bidPayLight,
  creationBidPayColorDark: DEFAULT_COLORS.creation.bidPayDark,
  creationAskReceiveColorLight: DEFAULT_COLORS.creation.askReceiveLight,
  creationAskReceiveColorDark: DEFAULT_COLORS.creation.askReceiveDark,
  tradeBlotterFlashColorLight: DEFAULT_COLORS.tradeBlotter.flashLight,
  tradeBlotterFlashColorDark: DEFAULT_COLORS.tradeBlotter.flashDark,
  creationFlashColorLight: DEFAULT_COLORS.creation.flashLight,
  creationFlashColorDark: DEFAULT_COLORS.creation.flashDark,
  pricesFlashColorLight: DEFAULT_COLORS.prices.flashLight,
  pricesFlashColorDark: DEFAULT_COLORS.prices.flashDark,
  toggleTableStripeTradeBlotter: () =>
    set((state) => {
      const newValue = !state.tableStripeTradeBlotterOn;
      localStorage.setItem(
        "tableStripeTradeBlotterOn",
        JSON.stringify(newValue),
      );
      return { tableStripeTradeBlotterOn: !state.tableStripeTradeBlotterOn };
    }),
  toggleTableStripePrices: () =>
    set((state) => {
      const newValue = !state.tableStripePricesOn;
      localStorage.setItem("tableStripePricesOn", JSON.stringify(newValue));
      return { tableStripePricesOn: !state.tableStripePricesOn };
    }),
  toggleTableStripeImport: () =>
    set((state) => {
      const newValue = !state.tableStripeImportOn;
      localStorage.setItem("tableStripeImportOn", JSON.stringify(newValue));
      return { tableStripeImportOn: !state.tableStripeImportOn };
    }),
  setFontSize: (size) =>
    set(() => {
      localStorage.setItem("tableFontSize", JSON.stringify(size));
      return { fontSize: size };
    }),
  setRowHeight: (height) =>
    set(() => {
      localStorage.setItem("tableRowHeight", JSON.stringify(height));
      return { rowHeight: height };
    }),
  setSoundQuoted: (sound) =>
    set(() => {
      localStorage.setItem("soundQuoted", JSON.stringify(sound));
      return { soundQuoted: sound };
    }),
  setSoundAwarded: (sound) =>
    set(() => {
      localStorage.setItem("soundAwarded", JSON.stringify(sound));
      return { soundAwarded: sound };
    }),
  setSoundExecuted: (sound) =>
    set(() => {
      localStorage.setItem("soundExecuted", JSON.stringify(sound));
      return { soundExecuted: sound };
    }),
  setSoundAffirmed: (sound) =>
    set(() => {
      localStorage.setItem("soundAffirmed", JSON.stringify(sound));
      return { soundAffirmed: sound };
    }),

  // Action Summary color setters
  setTradeBlotterColorLight: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterColorLight", JSON.stringify(color));
      return { tradeBlotterColorLight: color };
    }),
  settradeBlotterColorDark: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterColorDark", JSON.stringify(color));
      return { tradeBlotterColorDark: color };
    }),
  resetTradeBlotterColors: () =>
    set(() => {
      localStorage.setItem(
        "tradeBlotterColorLight",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.light),
      );
      localStorage.setItem(
        "tradeBlotterColorDark",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.dark),
      );
      return {
        tradeBlotterColorLight: DEFAULT_COLORS.tradeBlotter.light,
        tradeBlotterColorDark: DEFAULT_COLORS.tradeBlotter.dark,
      };
    }),

  // Prices color setters
  setPricesColorLight: (color) =>
    set(() => {
      localStorage.setItem("pricesColorLight", JSON.stringify(color));
      return { pricesColorLight: color };
    }),
  setPricesColorDark: (color) =>
    set(() => {
      localStorage.setItem("pricesColorDark", JSON.stringify(color));
      return { pricesColorDark: color };
    }),
  resetPricesColors: () =>
    set(() => {
      localStorage.setItem(
        "pricesColorLight",
        JSON.stringify(DEFAULT_COLORS.prices.light),
      );
      localStorage.setItem(
        "pricesColorDark",
        JSON.stringify(DEFAULT_COLORS.prices.dark),
      );
      return {
        pricesColorLight: DEFAULT_COLORS.prices.light,
        pricesColorDark: DEFAULT_COLORS.prices.dark,
      };
    }),

  // Import color setters
  setImportColorLight: (color) =>
    set(() => {
      localStorage.setItem("importColorLight", JSON.stringify(color));
      return { importColorLight: color };
    }),
  setImportColorDark: (color) =>
    set(() => {
      localStorage.setItem("importColorDark", JSON.stringify(color));
      return { importColorDark: color };
    }),
  resetImportColors: () =>
    set(() => {
      localStorage.setItem(
        "importColorLight",
        JSON.stringify(DEFAULT_COLORS.import.light),
      );
      localStorage.setItem(
        "importColorDark",
        JSON.stringify(DEFAULT_COLORS.import.dark),
      );
      return {
        importColorLight: DEFAULT_COLORS.import.light,
        importColorDark: DEFAULT_COLORS.import.dark,
      };
    }),

  // Bid/Ask color setters for Action Summary
  toggleTradeBlotterBidAsk: () =>
    set((state) => {
      const newValue = !state.tradeBlotterBidAskEnabled;
      localStorage.setItem(
        "tradeBlotterBidAskEnabled",
        JSON.stringify(newValue),
      );
      return { tradeBlotterBidAskEnabled: newValue };
    }),
  setTradeBlotterBidColorLight: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterBidColorLight", JSON.stringify(color));
      return { tradeBlotterBidColorLight: color };
    }),
  setTradeBlotterBidColorDark: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterBidColorDark", JSON.stringify(color));
      return { tradeBlotterBidColorDark: color };
    }),
  setTradeBlotterAskColorLight: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterAskColorLight", JSON.stringify(color));
      return { tradeBlotterAskColorLight: color };
    }),
  setTradeBlotterAskColorDark: (color) =>
    set(() => {
      localStorage.setItem("tradeBlotterAskColorDark", JSON.stringify(color));
      return { tradeBlotterAskColorDark: color };
    }),
  resetTradeBlotterBidAskColors: () =>
    set(() => {
      localStorage.setItem(
        "tradeBlotterBidColorLight",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.bidLight),
      );
      localStorage.setItem(
        "tradeBlotterBidColorDark",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.bidDark),
      );
      localStorage.setItem(
        "tradeBlotterAskColorLight",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.askLight),
      );
      localStorage.setItem(
        "tradeBlotterAskColorDark",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.askDark),
      );
      return {
        tradeBlotterBidColorLight: DEFAULT_COLORS.tradeBlotter.bidLight,
        tradeBlotterBidColorDark: DEFAULT_COLORS.tradeBlotter.bidDark,
        tradeBlotterAskColorLight: DEFAULT_COLORS.tradeBlotter.askLight,
        tradeBlotterAskColorDark: DEFAULT_COLORS.tradeBlotter.askDark,
      };
    }),

  // Creation color setters
  setCreationColorLight: (color) =>
    set(() => {
      localStorage.setItem("creationColorLight", JSON.stringify(color));
      return { creationColorLight: color };
    }),
  setCreationColorDark: (color) =>
    set(() => {
      localStorage.setItem("creationColorDark", JSON.stringify(color));
      return { creationColorDark: color };
    }),
  resetCreationColors: () =>
    set(() => {
      localStorage.setItem(
        "creationColorLight",
        JSON.stringify(DEFAULT_COLORS.creation.light),
      );
      localStorage.setItem(
        "creationColorDark",
        JSON.stringify(DEFAULT_COLORS.creation.dark),
      );
      return {
        creationColorLight: DEFAULT_COLORS.creation.light,
        creationColorDark: DEFAULT_COLORS.creation.dark,
      };
    }),

  // Direction color setters for Creation Table
  toggleCreationDirection: () =>
    set((state) => {
      const newValue = !state.creationDirectionEnabled;
      localStorage.setItem(
        "creationDirectionEnabled",
        JSON.stringify(newValue),
      );
      return { creationDirectionEnabled: newValue };
    }),
  setCreationBidPayColorLight: (color) =>
    set(() => {
      localStorage.setItem("creationBidPayColorLight", JSON.stringify(color));
      return { creationBidPayColorLight: color };
    }),
  setCreationBidPayColorDark: (color) =>
    set(() => {
      localStorage.setItem("creationBidPayColorDark", JSON.stringify(color));
      return { creationBidPayColorDark: color };
    }),
  setCreationAskReceiveColorLight: (color) =>
    set(() => {
      localStorage.setItem(
        "creationAskReceiveColorLight",
        JSON.stringify(color),
      );
      return { creationAskReceiveColorLight: color };
    }),
  setCreationAskReceiveColorDark: (color) =>
    set(() => {
      localStorage.setItem(
        "creationAskReceiveColorDark",
        JSON.stringify(color),
      );
      return { creationAskReceiveColorDark: color };
    }),
  resetCreationDirectionColors: () =>
    set(() => {
      localStorage.setItem(
        "creationBidPayColorLight",
        JSON.stringify(DEFAULT_COLORS.creation.bidPayLight),
      );
      localStorage.setItem(
        "creationBidPayColorDark",
        JSON.stringify(DEFAULT_COLORS.creation.bidPayDark),
      );
      localStorage.setItem(
        "creationAskReceiveColorLight",
        JSON.stringify(DEFAULT_COLORS.creation.askReceiveLight),
      );
      localStorage.setItem(
        "creationAskReceiveColorDark",
        JSON.stringify(DEFAULT_COLORS.creation.askReceiveDark),
      );
      return {
        creationBidPayColorLight: DEFAULT_COLORS.creation.bidPayLight,
        creationBidPayColorDark: DEFAULT_COLORS.creation.bidPayDark,
        creationAskReceiveColorLight: DEFAULT_COLORS.creation.askReceiveLight,
        creationAskReceiveColorDark: DEFAULT_COLORS.creation.askReceiveDark,
      };
    }),

  // Trade Blotter flash colour setters
  setTradeBlotterFlashColorLight: (color) =>
    set(() => {
      localStorage.setItem(
        "tradeBlotterFlashColorLight",
        JSON.stringify(color),
      );
      return { tradeBlotterFlashColorLight: color };
    }),
  setTradeBlotterFlashColorDark: (color) =>
    set(() => {
      localStorage.setItem(
        "tradeBlotterFlashColorDark",
        JSON.stringify(color),
      );
      return { tradeBlotterFlashColorDark: color };
    }),
  resetTradeBlotterFlashColors: () =>
    set(() => {
      localStorage.setItem(
        "tradeBlotterFlashColorLight",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.flashLight),
      );
      localStorage.setItem(
        "tradeBlotterFlashColorDark",
        JSON.stringify(DEFAULT_COLORS.tradeBlotter.flashDark),
      );
      return {
        tradeBlotterFlashColorLight: DEFAULT_COLORS.tradeBlotter.flashLight,
        tradeBlotterFlashColorDark: DEFAULT_COLORS.tradeBlotter.flashDark,
      };
    }),

  // Creation flash colour setters
  setCreationFlashColorLight: (color) =>
    set(() => {
      localStorage.setItem("creationFlashColorLight", JSON.stringify(color));
      return { creationFlashColorLight: color };
    }),
  setCreationFlashColorDark: (color) =>
    set(() => {
      localStorage.setItem("creationFlashColorDark", JSON.stringify(color));
      return { creationFlashColorDark: color };
    }),
  resetCreationFlashColors: () =>
    set(() => {
      localStorage.setItem(
        "creationFlashColorLight",
        JSON.stringify(DEFAULT_COLORS.creation.flashLight),
      );
      localStorage.setItem(
        "creationFlashColorDark",
        JSON.stringify(DEFAULT_COLORS.creation.flashDark),
      );
      return {
        creationFlashColorLight: DEFAULT_COLORS.creation.flashLight,
        creationFlashColorDark: DEFAULT_COLORS.creation.flashDark,
      };
    }),

  // Prices flash colour setters
  setPricesFlashColorLight: (color) =>
    set(() => {
      localStorage.setItem("pricesFlashColorLight", JSON.stringify(color));
      return { pricesFlashColorLight: color };
    }),
  setPricesFlashColorDark: (color) =>
    set(() => {
      localStorage.setItem("pricesFlashColorDark", JSON.stringify(color));
      return { pricesFlashColorDark: color };
    }),
  resetPricesFlashColors: () =>
    set(() => {
      localStorage.setItem(
        "pricesFlashColorLight",
        JSON.stringify(DEFAULT_COLORS.prices.flashLight),
      );
      localStorage.setItem(
        "pricesFlashColorDark",
        JSON.stringify(DEFAULT_COLORS.prices.flashDark),
      );
      return {
        pricesFlashColorLight: DEFAULT_COLORS.prices.flashLight,
        pricesFlashColorDark: DEFAULT_COLORS.prices.flashDark,
      };
    }),

  // Test flash triggers
  testFlashTradeBlotter: 0,
  triggerTestFlashTradeBlotter: () =>
    set((state) => ({ testFlashTradeBlotter: state.testFlashTradeBlotter + 1 })),
  testFlashCreation: 0,
  triggerTestFlashCreation: () =>
    set((state) => ({ testFlashCreation: state.testFlashCreation + 1 })),
  testFlashPrices: 0,
  triggerTestFlashPrices: () =>
    set((state) => ({ testFlashPrices: state.testFlashPrices + 1 })),

  initializeState: () => {
    // Used to update all the style vars from the local storage.
    const savedSettingTradeBlotterStripeOddOn = localStorage.getItem(
      "tableStripeTradeBlotterOn",
    );
    if (savedSettingTradeBlotterStripeOddOn !== null) {
      set({
        tableStripeTradeBlotterOn: JSON.parse(
          savedSettingTradeBlotterStripeOddOn,
        ),
      });
    }
    // PRICES
    const savedSettingPricesStripeOddOn = localStorage.getItem(
      "tableStripePricesOn",
    );
    if (savedSettingPricesStripeOddOn !== null) {
      set({
        tableStripePricesOn: JSON.parse(savedSettingPricesStripeOddOn),
      });
    }
    // IMPORT
    const savedSettingImportStripeOddOn = localStorage.getItem(
      "tableStripeImportOn",
    );
    if (savedSettingImportStripeOddOn !== null) {
      set({
        tableStripeImportOn: JSON.parse(savedSettingImportStripeOddOn),
      });
    }
    // FONT SIZE
    const savedFontSize = localStorage.getItem("tableFontSize");
    const validFontSizes = ["small", "medium", "large", "x-large"];
    if (savedFontSize !== null && validFontSizes.includes(JSON.parse(savedFontSize))) {
      set({
        fontSize: JSON.parse(savedFontSize),
      });
    }
    // ROW HEIGHT
    const savedRowHeight = localStorage.getItem("tableRowHeight");
    const validRowHeights = ["narrow", "standard", "expanded"];
    if (savedRowHeight !== null && validRowHeights.includes(JSON.parse(savedRowHeight))) {
      set({
        rowHeight: JSON.parse(savedRowHeight),
      });
    }
    // SOUND SETTINGS
    const savedSoundQuoted = localStorage.getItem("soundQuoted");
    if (savedSoundQuoted !== null) {
      set({
        soundQuoted: JSON.parse(savedSoundQuoted),
      });
    }
    const savedSoundAwarded = localStorage.getItem("soundAwarded");
    if (savedSoundAwarded !== null) {
      set({
        soundAwarded: JSON.parse(savedSoundAwarded),
      });
    }
    const savedSoundExecuted = localStorage.getItem("soundExecuted");
    if (savedSoundExecuted !== null) {
      set({
        soundExecuted: JSON.parse(savedSoundExecuted),
      });
    }
    const savedSoundAffirmed = localStorage.getItem("soundAffirmed");
    if (savedSoundAffirmed !== null) {
      set({
        soundAffirmed: JSON.parse(savedSoundAffirmed),
      });
    }
    // ACTION SUMMARY COLORS
    const savedtradeBlotterColorLight = localStorage.getItem(
      "tradeBlotterColorLight",
    );
    if (savedtradeBlotterColorLight !== null) {
      set({
        tradeBlotterColorLight: JSON.parse(savedtradeBlotterColorLight),
      });
    }
    const savedtradeBlotterColorDark = localStorage.getItem(
      "tradeBlotterColorDark",
    );
    if (savedtradeBlotterColorDark !== null) {
      set({
        tradeBlotterColorDark: JSON.parse(savedtradeBlotterColorDark),
      });
    }
    // PRICES COLORS
    const savedPricesColorLight = localStorage.getItem("pricesColorLight");
    if (savedPricesColorLight !== null) {
      set({
        pricesColorLight: JSON.parse(savedPricesColorLight),
      });
    }
    const savedPricesColorDark = localStorage.getItem("pricesColorDark");
    if (savedPricesColorDark !== null) {
      set({
        pricesColorDark: JSON.parse(savedPricesColorDark),
      });
    }
    // IMPORT COLORS
    const savedImportColorLight = localStorage.getItem("importColorLight");
    if (savedImportColorLight !== null) {
      set({
        importColorLight: JSON.parse(savedImportColorLight),
      });
    }
    const savedImportColorDark = localStorage.getItem("importColorDark");
    if (savedImportColorDark !== null) {
      set({
        importColorDark: JSON.parse(savedImportColorDark),
      });
    }
    // BID/ASK COLORS FOR ACTION SUMMARY
    const savedBidAskEnabled = localStorage.getItem(
      "tradeBlotterBidAskEnabled",
    );
    if (savedBidAskEnabled !== null) {
      set({
        tradeBlotterBidAskEnabled: JSON.parse(savedBidAskEnabled),
      });
    }
    const savedBidColorLight = localStorage.getItem(
      "tradeBlotterBidColorLight",
    );
    if (savedBidColorLight !== null) {
      set({
        tradeBlotterBidColorLight: JSON.parse(savedBidColorLight),
      });
    }
    const savedBidColorDark = localStorage.getItem("tradeBlotterBidColorDark");
    if (savedBidColorDark !== null) {
      set({
        tradeBlotterBidColorDark: JSON.parse(savedBidColorDark),
      });
    }
    const savedAskColorLight = localStorage.getItem(
      "tradeBlotterAskColorLight",
    );
    if (savedAskColorLight !== null) {
      set({
        tradeBlotterAskColorLight: JSON.parse(savedAskColorLight),
      });
    }
    const savedAskColorDark = localStorage.getItem("tradeBlotterAskColorDark");
    if (savedAskColorDark !== null) {
      set({
        tradeBlotterAskColorDark: JSON.parse(savedAskColorDark),
      });
    }
    // CREATION COLORS
    const savedCreationColorLight = localStorage.getItem("creationColorLight");
    if (savedCreationColorLight !== null) {
      set({
        creationColorLight: JSON.parse(savedCreationColorLight),
      });
    }
    const savedCreationColorDark = localStorage.getItem("creationColorDark");
    if (savedCreationColorDark !== null) {
      set({
        creationColorDark: JSON.parse(savedCreationColorDark),
      });
    }
    // DIRECTION COLORS FOR CREATION TABLE
    const savedDirectionEnabled = localStorage.getItem(
      "creationDirectionEnabled",
    );
    if (savedDirectionEnabled !== null) {
      set({
        creationDirectionEnabled: JSON.parse(savedDirectionEnabled),
      });
    }
    const savedBidPayColorLight = localStorage.getItem(
      "creationBidPayColorLight",
    );
    if (savedBidPayColorLight !== null) {
      set({
        creationBidPayColorLight: JSON.parse(savedBidPayColorLight),
      });
    }
    const savedBidPayColorDark = localStorage.getItem(
      "creationBidPayColorDark",
    );
    if (savedBidPayColorDark !== null) {
      set({
        creationBidPayColorDark: JSON.parse(savedBidPayColorDark),
      });
    }
    const savedAskReceiveColorLight = localStorage.getItem(
      "creationAskReceiveColorLight",
    );
    if (savedAskReceiveColorLight !== null) {
      set({
        creationAskReceiveColorLight: JSON.parse(savedAskReceiveColorLight),
      });
    }
    const savedAskReceiveColorDark = localStorage.getItem(
      "creationAskReceiveColorDark",
    );
    if (savedAskReceiveColorDark !== null) {
      set({
        creationAskReceiveColorDark: JSON.parse(savedAskReceiveColorDark),
      });
    }
    // FLASH COLOURS FOR TRADE BLOTTER
    const savedTradeBlotterFlashLight = localStorage.getItem(
      "tradeBlotterFlashColorLight",
    );
    if (savedTradeBlotterFlashLight !== null) {
      set({
        tradeBlotterFlashColorLight: JSON.parse(savedTradeBlotterFlashLight),
      });
    }
    const savedTradeBlotterFlashDark = localStorage.getItem(
      "tradeBlotterFlashColorDark",
    );
    if (savedTradeBlotterFlashDark !== null) {
      set({
        tradeBlotterFlashColorDark: JSON.parse(savedTradeBlotterFlashDark),
      });
    }
    // FLASH COLOURS FOR CREATION TABLE
    const savedCreationFlashLight = localStorage.getItem(
      "creationFlashColorLight",
    );
    if (savedCreationFlashLight !== null) {
      set({
        creationFlashColorLight: JSON.parse(savedCreationFlashLight),
      });
    }
    const savedCreationFlashDark = localStorage.getItem(
      "creationFlashColorDark",
    );
    if (savedCreationFlashDark !== null) {
      set({
        creationFlashColorDark: JSON.parse(savedCreationFlashDark),
      });
    }
    // FLASH COLOURS FOR PRICES TABLE
    const savedPricesFlashLight = localStorage.getItem(
      "pricesFlashColorLight",
    );
    if (savedPricesFlashLight !== null) {
      set({
        pricesFlashColorLight: JSON.parse(savedPricesFlashLight),
      });
    }
    const savedPricesFlashDark = localStorage.getItem(
      "pricesFlashColorDark",
    );
    if (savedPricesFlashDark !== null) {
      set({
        pricesFlashColorDark: JSON.parse(savedPricesFlashDark),
      });
    }
  },
}));
export default useTableUIStore;
